<template>
  <component
    :is="tag"
    :class="[
      'app-logo',
      `app-logo--${size}`,
      { 'app-logo--image': isImageMode },
    ]"
    :style="imageStyle"
    v-bind="$attrs"
  >
    <img
      v-if="isImageMode"
      :src="resolvedSrc"
      :alt="alt"
      class="app-logo__image"
      decoding="async"
    />
    <LogoMark v-else :size="markSize" :uid="logoUid" class="app-logo__mark" />
    <span v-if="showText && !isImageMode" class="app-logo__text">{{
      logoText
    }}</span>
  </component>
</template>

<script setup>
import { computed } from "vue";
import LogoMark from "@/components/mkdf/LogoMark.vue";
import {
  LOGO_TEXT,
  getLogoImageBounds,
  resolveLogoImageSrc,
} from "@/config/brand";

const props = defineProps({
  showText: { type: Boolean, default: true },
  size: {
    type: String,
    default: "md",
    validator: (v) => ["sm", "md", "lg"].includes(v),
  },
  tag: { type: String, default: "div" },
  /** `mark` = icon + text, `image` = bounded logo image (wide or square). */
  variant: {
    type: String,
    default: "mark",
    validator: (v) => ["mark", "image"].includes(v),
  },
  src: { type: String, default: "" },
  alt: { type: String, default: LOGO_TEXT },
});

const markSize = computed(() => {
  const sizes = { sm: 32, md: 36, lg: 44 };
  return sizes[props.size];
});

const logoUid = computed(() => `mkdf-${props.size}`);
const logoText = LOGO_TEXT;
const isImageMode = computed(
  () => props.variant === "image" || Boolean(props.src)
);
const resolvedSrc = computed(() => resolveLogoImageSrc(props.src));
const imageBounds = computed(() => getLogoImageBounds(props.size));

const imageStyle = computed(() => {
  if (!isImageMode.value) {
    return undefined;
  }

  const { maxHeight, maxWidth } = imageBounds.value;
  return {
    "--logo-max-height": `${maxHeight}px`,
    "--logo-max-width": `${maxWidth}px`,
  };
});
</script>

<style scoped>
.app-logo {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: var(--content-heading);
}

.app-logo--image {
  gap: 0;
  max-width: var(--logo-max-width);
}

.app-logo__mark {
  filter: drop-shadow(0 2px 6px rgba(84, 86, 232, 0.22));
}

.app-logo__image {
  display: block;
  width: auto;
  height: auto;
  max-height: var(--logo-max-height);
  max-width: var(--logo-max-width);
  object-fit: contain;
  object-position: center;
}

.app-logo__text {
  font-weight: 700;
  letter-spacing: -0.5px;
  line-height: 1.2;
  white-space: nowrap;
}

.app-logo--sm .app-logo__text {
  font-size: 1rem;
}

.app-logo--md .app-logo__text {
  font-size: 1.125rem;
}

.app-logo--lg .app-logo__text {
  font-size: 1.25rem;
}
</style>
